// INSIGHTS-DRE: deriva indicadores e comparações a partir de DUAS respostas do
// endpoint /dre (período A e B). Não toca no motor — só lê o que ele já devolve.
// Convenção do backend: despesas/custos vêm com valor NEGATIVo; receitas positivas.

export interface DreResp {
  receitas: {
    recBruta: number;
    recLiquida?: number;
    recLiqComercial: number;
    areas: Record<string, number>;
  };
  custos: { custosOp: number; detalhe: Record<string, number> };
  lucroBruto: number;
  despesas: {
    despComercial: number;
    despAdmin: number;
    outrasDespOp: number;
    detalhe: { comercial: Record<string, number>; admin: Record<string, number>; outras: Record<string, number> };
  };
  resultadoOp: number; // = EBITDA (sem depreciação lançada)
  impostos: { total: number };
  deducoes: { total: number };
  lucroLiq: number;
  // grupo INVESTIMENTOS (CAPEX) — valores NEGATIVOS; lucroFinal = lucroLiq + investimentos
  investimentos?: { total: number; detalhe: Record<string, number> };
  lucroFinal: number;
}

export const receitaLiquida = (d: DreResp) => d.receitas.recLiquida ?? d.receitas.recLiqComercial ?? 0;

export interface KpiComparado {
  chave: string;
  titulo: string;
  valorA: number;
  valorB: number;
  deltaAbs: number;
  deltaPct: number | null; // null quando base A é zero
  margemA: number | null;  // % sobre receita líquida do período
  margemB: number | null;
  // faixa de referência de mercado (só EBITDA), em % — [min, max]
  refMercado?: [number, number];
}

function delta(a: number, b: number) {
  const deltaAbs = b - a;
  const deltaPct = a !== 0 ? (deltaAbs / Math.abs(a)) * 100 : null;
  return { deltaAbs, deltaPct };
}

export function kpisComparados(a: DreResp, b: DreResp): KpiComparado[] {
  const rlA = receitaLiquida(a), rlB = receitaLiquida(b);
  const margem = (v: number, base: number) => (base ? (v / base) * 100 : null);
  const mk = (chave: string, titulo: string, va: number, vb: number, refMercado?: [number, number]): KpiComparado => ({
    chave, titulo, valorA: va, valorB: vb, ...delta(va, vb),
    margemA: margem(va, rlA), margemB: margem(vb, rlB), refMercado,
  });
  const kpiReceita = mk('receita', 'Receita Líquida', rlA, rlB);
  kpiReceita.margemA = null; kpiReceita.margemB = null; // base — margem sobre si = 100%, não faz sentido
  return [
    kpiReceita,
    mk('lucroBruto', 'Lucro Bruto', a.lucroBruto, b.lucroBruto),
    // resultado operacional = EBITDA enquanto não houver depreciação lançada
    mk('ebitda', 'EBITDA', a.resultadoOp, b.resultadoOp, [10, 25]),
    mk('lucroLiquido', 'Lucro Líquido', a.lucroLiq, b.lucroLiq),
  ];
}

// Composição: cada grande grupo como % da receita líquida (custo/despesa em
// magnitude positiva pra leitura). Serve pra barra comparativa A×B.
export interface LinhaComposicao {
  grupo: string;
  valorA: number; // magnitude (positivo)
  valorB: number;
  pctA: number | null;
  pctB: number | null;
}

export function composicao(a: DreResp, b: DreResp): LinhaComposicao[] {
  const rlA = receitaLiquida(a), rlB = receitaLiquida(b);
  const mag = (v: number) => Math.abs(v || 0);
  const grupos: Array<[string, (d: DreResp) => number]> = [
    ['Custos Operacionais', d => d.custos.custosOp],
    ['Despesas Comerciais', d => d.despesas.despComercial],
    ['Despesas Administrativas', d => d.despesas.despAdmin],
    ['Impostos', d => d.impostos.total],
    ['Deduções (retenções)', d => d.deducoes.total],
  ];
  return grupos.map(([grupo, sel]) => {
    const va = mag(sel(a)), vb = mag(sel(b));
    return { grupo, valorA: va, valorB: vb, pctA: rlA ? (va / rlA) * 100 : null, pctB: rlB ? (vb / rlB) * 100 : null };
  });
}

// Ponte Receita → EBITDA → Lucro Líquido (para o waterfall na apresentação),
// calculada no período B (o mais recente).
export interface PassoPonte { rotulo: string; valor: number; tipo: 'base' | 'soma' | 'subtrai' | 'total'; }

export function ponte(d: DreResp): PassoPonte[] {
  const rl = receitaLiquida(d);
  return [
    { rotulo: 'Receita Líquida', valor: rl, tipo: 'base' },
    { rotulo: 'Custos Operacionais', valor: d.custos.custosOp, tipo: 'subtrai' },
    { rotulo: 'Lucro Bruto', valor: d.lucroBruto, tipo: 'total' },
    { rotulo: 'Desp. Comerciais', valor: d.despesas.despComercial, tipo: 'subtrai' },
    { rotulo: 'Desp. Administrativas', valor: d.despesas.despAdmin, tipo: 'subtrai' },
    { rotulo: 'EBITDA', valor: d.resultadoOp, tipo: 'total' },
    { rotulo: 'Impostos + Deduções', valor: d.impostos.total + d.deducoes.total, tipo: 'subtrai' },
    { rotulo: 'Lucro Líquido', valor: d.lucroLiq, tipo: 'total' },
    { rotulo: 'Reinvestimento na empresa', valor: d.investimentos?.total ?? 0, tipo: 'subtrai' },
    { rotulo: 'Lucro Final', valor: d.lucroFinal ?? d.lucroLiq, tipo: 'total' },
  ];
}

// Destino do lucro (A+B): quanto do lucro líquido voltou pra empresa como
// investimento (equipamentos, software, estrutura) e quanto sobrou de fato
// (lucro final). Investimentos chegam NEGATIVOS do motor — aqui em magnitude.
export interface Reinvestimento {
  lucroLiq: number;
  investido: number;             // magnitude positiva
  lucroFinal: number;
  pctReinvestido: number | null; // % do lucro líquido; null se lucro <= 0
  topCategorias: Array<{ nome: string; valor: number }>;
}

export function reinvestimento(a: DreResp, b: DreResp): Reinvestimento {
  const lucroLiq = a.lucroLiq + b.lucroLiq;
  const investido = Math.abs((a.investimentos?.total ?? 0) + (b.investimentos?.total ?? 0));
  const lucroFinal = (a.lucroFinal ?? a.lucroLiq) + (b.lucroFinal ?? b.lucroLiq);
  const cats: Record<string, number> = {};
  for (const d of [a, b]) {
    for (const [k, v] of Object.entries(d.investimentos?.detalhe || {})) {
      cats[k] = (cats[k] || 0) + Math.abs(Number(v) || 0);
    }
  }
  const topCategorias = Object.entries(cats)
    .map(([nome, valor]) => ({ nome, valor }))
    .filter(c => c.valor >= 0.01)
    .sort((x, y) => y.valor - x.valor)
    .slice(0, 5);
  return {
    lucroLiq, investido, lucroFinal,
    pctReinvestido: lucroLiq > 0 ? (investido / lucroLiq) * 100 : null,
    topCategorias,
  };
}

// Top movimentos de DESPESA por categoria (o que mais subiu/caiu de gasto).
// Junta custos + as 3 famílias de despesa; magnitude positiva = gasto.
export interface Movimento { nome: string; valorA: number; valorB: number; delta: number; deltaPct: number | null; }

function achatarDespesas(d: DreResp): Record<string, number> {
  const out: Record<string, number> = {};
  const add = (m: Record<string, number> = {}) => {
    for (const [k, v] of Object.entries(m)) out[k] = (out[k] || 0) + Math.abs(Number(v) || 0);
  };
  add(d.custos.detalhe);
  add(d.despesas.detalhe.comercial);
  add(d.despesas.detalhe.admin);
  add(d.despesas.detalhe.outras);
  return out;
}

export function topMovimentosDespesa(a: DreResp, b: DreResp, n = 6): { subiram: Movimento[]; cairam: Movimento[] } {
  const ma = achatarDespesas(a), mb = achatarDespesas(b);
  const chaves = Array.from(new Set([...Object.keys(ma), ...Object.keys(mb)]));
  const movs: Movimento[] = [];
  for (const nome of chaves) {
    const va = ma[nome] || 0, vb = mb[nome] || 0;
    const dl = vb - va;
    if (Math.abs(dl) < 0.01) continue;
    movs.push({ nome, valorA: va, valorB: vb, delta: dl, deltaPct: va ? (dl / va) * 100 : null });
  }
  const subiram = [...movs].filter(m => m.delta > 0).sort((x, y) => y.delta - x.delta).slice(0, n);
  const cairam = [...movs].filter(m => m.delta < 0).sort((x, y) => x.delta - y.delta).slice(0, n);
  return { subiram, cairam };
}

// Receita por área — comparação A×B (só áreas com valor em algum período).
const ROTULO_AREA: Record<string, string> = {
  topografia: 'Topografia', hidrometria: 'Hidrometria', consultoria: 'Consultoria',
  projeto: 'Projeto', fiscalizacao: 'Fiscalização', segbarr: 'Seg. Barragens',
  sem_setor: 'Sem setor',
};

export interface AreaComparada { area: string; rotulo: string; valorA: number; valorB: number; delta: number; deltaPct: number | null; }

export function receitaPorArea(a: DreResp, b: DreResp): AreaComparada[] {
  const chaves = Array.from(new Set([...Object.keys(a.receitas.areas || {}), ...Object.keys(b.receitas.areas || {})]));
  const out: AreaComparada[] = [];
  for (const area of chaves) {
    const va = a.receitas.areas[area] || 0, vb = b.receitas.areas[area] || 0;
    if (Math.abs(va) < 0.01 && Math.abs(vb) < 0.01) continue;
    out.push({ area, rotulo: ROTULO_AREA[area] || area, valorA: va, valorB: vb, delta: vb - va, deltaPct: va ? ((vb - va) / va) * 100 : null });
  }
  return out.sort((x, y) => y.valorB - x.valorB);
}

// Narrativa automática: 2-3 frases de destaque geradas dos números.
export function narrativa(a: DreResp, b: DreResp, rotA: string, rotB: string): string[] {
  const rlA = receitaLiquida(a), rlB = receitaLiquida(b);
  const dRec = rlA ? ((rlB - rlA) / rlA) * 100 : 0;
  const custoA = Math.abs(a.custos.custosOp), custoB = Math.abs(b.custos.custosOp);
  const dCusto = custoA ? ((custoB - custoA) / custoA) * 100 : 0;
  const margEbA = rlA ? (a.resultadoOp / rlA) * 100 : 0;
  const margEbB = rlB ? (b.resultadoOp / rlB) * 100 : 0;
  const frases: string[] = [];
  const dir = (v: number) => (v >= 0 ? 'subiu' : 'caiu');
  frases.push(
    `Receita líquida ${dir(dRec)} ${Math.abs(dRec).toFixed(1)}% de ${rotA} para ${rotB} ` +
    `(${(rlA / 1000).toFixed(0)}k → ${(rlB / 1000).toFixed(0)}k).`
  );
  if (dRec < 0 && Math.abs(dCusto) < Math.abs(dRec)) {
    frases.push(
      `Os custos operacionais recuaram só ${Math.abs(dCusto).toFixed(1)}% enquanto a receita caiu ${Math.abs(dRec).toFixed(1)}% ` +
      `— com a estrutura de custo quase fixa, a margem comprime mais que proporcionalmente (alavancagem operacional).`
    );
  } else if (dRec >= 0) {
    frases.push(`Custos operacionais variaram ${dCusto >= 0 ? '+' : ''}${dCusto.toFixed(1)}% no período.`);
  }
  frases.push(
    `Margem EBITDA passou de ${margEbA.toFixed(0)}% para ${margEbB.toFixed(0)}% ` +
    `(referência de mercado para consultoria de engenharia: 10–25%).`
  );
  const r = reinvestimento(a, b);
  if (r.investido > 0.01 && r.lucroLiq > 0) {
    frases.push(
      `O lucro líquido não é integralmente distribuído: do acumulado de ${(r.lucroLiq / 1000).toFixed(0)}k, ` +
      `${(r.investido / 1000).toFixed(0)}k (${r.pctReinvestido!.toFixed(0)}%) foram reinvestidos na própria empresa ` +
      `— equipamentos, tecnologia e estrutura que sustentam o crescimento. ` +
      `Lucro final, após o reinvestimento: ${(r.lucroFinal / 1000).toFixed(0)}k.`
    );
  }
  return frases;
}
